import { useCallback } from "react";
import pillboxService from "../services/pillbox.service";
import type {
  CreateMedicineItemPayload,
  MedicineInfo,
  MedicineItemType,
  PillboxHistoryEntry,
  PillboxItem,
  PillboxPendingChange,
  ScheduleDay,
} from "../types/pillbox";

export function usePillbox(memberId: string) {
  const fetchLedger = useCallback(
    (status?: string): Promise<PillboxItem[]> => {
      if (!memberId) return Promise.resolve([]);
      return pillboxService.getLedger(memberId, status);
    },
    [memberId]
  );

  const fetchSchedule = useCallback(
    (startDate?: string, days?: number): Promise<ScheduleDay[]> => {
      if (!memberId) return Promise.resolve([]);
      return pillboxService.getSchedule(memberId, startDate, days);
    },
    [memberId]
  );

  const addMedicine = useCallback(
    (
      itemType: MedicineItemType,
      payload: CreateMedicineItemPayload
    ): Promise<PillboxItem> => {
      return pillboxService.addMedicine(memberId, itemType, payload);
    },
    [memberId]
  );

  const fetchItem = useCallback(
    (itemType: MedicineItemType, itemId: string): Promise<PillboxItem> => {
      return pillboxService.getItem(memberId, itemType, itemId);
    },
    [memberId]
  );

  const fetchHistory = useCallback(
    (
      itemType: MedicineItemType,
      itemId: string
    ): Promise<PillboxHistoryEntry[]> => {
      return pillboxService.getHistory(memberId, itemType, itemId);
    },
    [memberId]
  );

  const updateItem = useCallback(
    (
      itemType: MedicineItemType,
      itemId: string,
      payload: CreateMedicineItemPayload
    ): Promise<PillboxItem> => {
      return pillboxService.updateItem(memberId, itemType, itemId, payload);
    },
    [memberId]
  );

  const resumeItem = useCallback(
    (
      itemType: MedicineItemType,
      pastItemId: string,
      payload: CreateMedicineItemPayload
    ): Promise<PillboxItem> => {
      return pillboxService.resumeItem(memberId, itemType, pastItemId, payload);
    },
    [memberId]
  );

  const fetchPending = useCallback((): Promise<PillboxPendingChange[]> => {
    if (!memberId) return Promise.resolve([]);
    return pillboxService.listPending(memberId);
  }, [memberId]);

  const confirmPending = useCallback(
    (
      pendingId: string,
      edits: CreateMedicineItemPayload
    ): Promise<PillboxItem> => {
      return pillboxService.confirmPending(memberId, pendingId, edits);
    },
    [memberId]
  );

  const rejectPending = useCallback(
    (pendingId: string): Promise<void> => {
      return pillboxService.rejectPending(memberId, pendingId);
    },
    [memberId]
  );

  const stopItem = useCallback(
    (itemType: MedicineItemType, itemId: string): Promise<PillboxItem> => {
      return pillboxService.stopItem(memberId, itemType, itemId);
    },
    [memberId]
  );

  const deleteItem = useCallback(
    (itemType: MedicineItemType, itemId: string): Promise<void> => {
      return pillboxService.deleteItem(memberId, itemType, itemId);
    },
    [memberId]
  );

  const toggleRefill = useCallback(
    (
      itemType: MedicineItemType,
      itemId: string,
      needsRefill: boolean
    ): Promise<PillboxItem> => {
      return pillboxService.toggleRefill(
        memberId,
        itemType,
        itemId,
        needsRefill
      );
    },
    [memberId]
  );

  const downloadSharePdf = useCallback(
    (view: "schedule" | "list", date?: string): Promise<Blob> => {
      return pillboxService.downloadSharePdf(memberId, view, date);
    },
    [memberId]
  );

  const fetchShoppingList = useCallback((): Promise<PillboxItem[]> => {
    return pillboxService.getShoppingList();
  }, []);

  const downloadShoppingListPdf = useCallback((): Promise<Blob> => {
    return pillboxService.downloadShoppingListPdf();
  }, []);

  const fetchMedicineInfo = useCallback(
    (name: string): Promise<MedicineInfo> => {
      return pillboxService.getMedicineInfo(name);
    },
    []
  );

  return {
    fetchLedger,
    fetchSchedule,
    addMedicine,
    fetchItem,
    fetchHistory,
    updateItem,
    resumeItem,
    fetchPending,
    confirmPending,
    rejectPending,
    stopItem,
    deleteItem,
    toggleRefill,
    downloadSharePdf,
    fetchShoppingList,
    downloadShoppingListPdf,
    fetchMedicineInfo,
  };
}
